/**
 * Release Post Schema
 *
 * Zod validation schema for release post generator form.
 * Story: RELEASE-002 - Multi-Video Support & Manual Transcript Input
 */
import { z } from 'zod'

export const videoEntrySchema = z.object({
  loom_url: z.string().optional(),
  transcript: z.string().min(10, 'Transcript must be at least 10 characters'),
  title: z.string().optional(),
  description: z.string().optional(),
  input_method: z.enum(['auto_extract', 'manual_paste']),
})
  .refine(
    (data) => {
      // If auto_extract, loom_url is required
      if (data.input_method === 'auto_extract') {
        return !!data.loom_url && data.loom_url.includes('loom.com')
      }
      return true
    },
    {
      message: 'Loom URL is required for auto-extract mode',
      path: ['loom_url'],
    }
  )

export const releasePostSchema = z.object({
  videos: z.array(videoEntrySchema)
    .min(1, 'At least one video is required')
    .max(5, 'Maximum 5 videos allowed'),
  release_version: z
    .string()
    .regex(/^v?\d+\.\d+\.\d+$/, 'Version must be in format v1.2.3 or 1.2.3')
    .optional()
    .or(z.literal('')), // Allow empty string which backend will auto-generate
  release_date: z
    .string()
    .min(1, 'Release date is required'),
  release_type: z.enum(['major', 'minor', 'patch', 'feature'], {
    required_error: 'Release type is required',
  }),
  feature_summary: z.string().optional(),
  target_audience: z.enum(['all_users', 'admins', 'developers'], {
    required_error: 'Target audience is required',
  }),
})

export type VideoEntry = z.infer<typeof videoEntrySchema>
export type ReleasePostFormData = z.infer<typeof releasePostSchema>
